import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  confirmSlackInstallation,
  createSlackIdentityLinkUrl,
  createSlackInstallUrl,
  disconnectSlackInstallation,
  getPendingSlackInstallation,
  getSlackIdentity,
  getSlackInstallation,
  setSlackDeliveryEnabled,
  unlinkSlackIdentity,
} from '../api/slack.api';
import { PendingSlackInstallation, SlackConnection, SlackIdentityStatus } from '../types/Slack';
import { queryKeys } from './queryKeys';
import { queryClient } from '../queryClient';
import { QUERY_CONFIG } from '../constants/queryConfig';

/**
 * Read what a pending Slack token refers to, without spending it.
 *
 * `retry: false` is deliberate: the server answers the same 404 for an unknown, expired,
 * or already-spent token, and that is a terminal state the confirmation screen must show
 * immediately - retrying it just delays the same error. `staleTime: Infinity` because the
 * record is immutable for its lifetime.
 */
export const useGetPendingSlackInstallation = (pendingToken: string | null) => {
  return useQuery<PendingSlackInstallation, AxiosError>({
    queryKey: queryKeys.slackPendingInstallation(pendingToken ?? ''),
    queryFn: async () => (await getPendingSlackInstallation(pendingToken as string)).data,
    enabled: !!pendingToken,
    retry: false,
    staleTime: Infinity,
  });
};

/**
 * Binds the pending Slack workspace to an organization. `retry: false`: a 409 (already
 * bound to another organization) or an expired/spent token must never be retried
 * automatically - an automatic retry loop against either would hammer the endpoint and
 * confuse the user with a screen that appears stuck.
 */
export const useConfirmSlackInstallation = () => {
  return useMutation<void, AxiosError, { organizationId: number; pendingToken: string }>({
    mutationFn: async ({ organizationId, pendingToken }) => {
      await confirmSlackInstallation({ organizationId, pendingToken });
    },
    retry: false,
  });
};

/** The organization's Slack workspace connection. MEMBER-readable. */
export const useGetSlackInstallation = (organizationId: number | null) => {
  return useQuery<SlackConnection, AxiosError>({
    queryKey: queryKeys.slackInstallation(organizationId as number),
    queryFn: async () => (await getSlackInstallation(organizationId as number)).data,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/**
 * The caller's own link status.
 *
 * Deliberately NOT given `QUERY_CONFIG.STALE_TIME` (24h) the way the org-scoped
 * queries are: the user arrives here straight off the Slack callback redirect, and a
 * day-stale answer would show them "Not connected" on the very page that was supposed
 * to confirm the link they just completed.
 */
export const useGetSlackIdentity = (organizationId: number | null) => {
  return useQuery<SlackIdentityStatus, AxiosError>({
    queryKey: queryKeys.slackIdentity(organizationId as number),
    queryFn: async () => (await getSlackIdentity(organizationId as number)).data,
    enabled: !!organizationId,
  });
};

/** Resolves the slack.com URL the browser should be sent to. */
export const useConnectSlackMutation = () => {
  return useMutation<string, AxiosError, number>({
    mutationFn: async (organizationId: number) => (await createSlackInstallUrl(organizationId)).data.installUrl,
  });
};

export const useDisconnectSlackMutation = () => {
  return useMutation<void, AxiosError, number>({
    mutationFn: async (organizationId: number) => {
      await disconnectSlackInstallation(organizationId);
    },
    onSuccess: (_data, organizationId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.slackInstallation(organizationId) });
      // Disconnecting the workspace changes what every identity row under it can do,
      // so the caller's own link status is stale too.
      void queryClient.invalidateQueries({ queryKey: queryKeys.slackIdentity(organizationId) });
    },
  });
};

/** Resolves the "Sign in with Slack" URL for the caller's own account. */
export const useLinkSlackIdentityMutation = () => {
  return useMutation<string, AxiosError, number>({
    mutationFn: async (organizationId: number) => (await createSlackIdentityLinkUrl(organizationId)).data.authorizeUrl,
  });
};

export const useUnlinkSlackIdentityMutation = () => {
  return useMutation<void, AxiosError, number>({
    mutationFn: async (organizationId: number) => {
      await unlinkSlackIdentity(organizationId);
    },
    onSuccess: (_data, organizationId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.slackIdentity(organizationId) });
    },
  });
};

/**
 * The caller's own delivery switch. Never touches a system-imposed pause: this is
 * user intent only, and the server keeps that distinction on its side too.
 */
export const useSetSlackDeliveryMutation = () => {
  return useMutation<void, AxiosError, { organizationId: number; enabled: boolean }>({
    mutationFn: async ({ organizationId, enabled }) => {
      await setSlackDeliveryEnabled(organizationId, enabled);
    },
    onSuccess: (_data, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.slackIdentity(organizationId) });
    },
  });
};
